/**
 * The chat's state and every way it changes, as one pure function.
 *
 * `ChatProvider` owns the only instance: socket listeners and request answers
 * dispatch actions here and do nothing else, so each thing that can happen to
 * the chat - a push, an answer, a click - is one case below. A new feature is
 * a new case, not a new effect (#118).
 *
 * History is held per channel, so switching back to a channel shows it at
 * once while its join refreshes it, and nothing that arrives for one channel
 * can land in another's list.
 */

import type {
  ChatChannel,
  ChatChannelView,
  ChatGetMessagesReply,
  ChatInitialStatePayload,
  ChatMessage,
  ChatStartDmReply
} from '@shared/schemas/chat.schema.js';
import type { ClientChatMessage } from '../types/chat.types';

/** Where the join of a channel is: the answer carries its latest history. */
export type ChannelJoinStatus = 'joining' | 'joined' | 'failed';

/** Where loading the page before the oldest held message is. */
export type OlderMessagesStatus = 'idle' | 'loading' | 'failed';

/** What the client holds of one channel. */
export interface ChannelHistory {
  /** Oldest first: the server's history, then anything that arrived since. */
  messages: ClientChatMessage[];
  /** Whether the server has messages older than the ones held. */
  hasMore: boolean;
  /** The cursor for the next older page: the first message of the last window or page. */
  oldestMessageId?: string;
  join: ChannelJoinStatus;
  older: OlderMessagesStatus;
  /**
   * The message the "New Messages" divider goes before: the first message
   * from someone else created after the member's `lastRead`, placed from the
   * answer to the join that opened the channel. Undefined until that answer
   * arrives, null when nothing was new. A re-join of the channel on screen
   * (after a reconnect) keeps it, so messages read live never get one.
   */
  dividerBeforeId?: string | null;
}

export interface ChatState {
  channels: ChatChannel[];
  /** Unread messages per channel; the channel on screen never has any. */
  unreadCounts: Record<string, number>;
  /** Whether the chat drawer is open: a channel is only viewed while it is. */
  isOpen: boolean;
  /** The channel the drawer shows, kept while it is closed to reopen on. */
  activeChannelId: string | null;
  histories: Record<string, ChannelHistory>;
  /** A chat failure with no message or channel to show it on (a DM that did not open). */
  notice: string | null;
}

export type ChatAction =
  /** A new socket, or none: nothing of the last one's chat carries over. */
  | { type: 'reset' }
  /** `chat:initial_state`: the channel list and the full unread recount. */
  | { type: 'initialState'; payload: ChatInitialStatePayload }
  /** The drawer opens, on `channelId` when there is one to show. */
  | { type: 'drawerOpened'; channelId: string | null }
  | { type: 'drawerClosed' }
  /** The user picks a channel; its join is on its way. */
  | { type: 'channelSelected'; channelId: string }
  /** The channel on screen is joined again (after a reconnect, or a retry). */
  | { type: 'joinRequested'; channelId: string }
  | { type: 'joinSucceeded'; view: ChatChannelView; selfId: string }
  | { type: 'joinFailed'; channelId: string }
  /** A DM opened: listed, and shown when the drawer is still open. */
  | { type: 'dmStarted'; reply: ChatStartDmReply; selfId: string }
  /** Our message, shown at once with `status: 'sending'` and a `temp-…` id. */
  | { type: 'messageSent'; channelId: string; message: ClientChatMessage }
  | { type: 'sendSucceeded'; channelId: string; tempId: string; message: ChatMessage }
  | { type: 'sendFailed'; channelId: string; tempId: string }
  /** A failed message is sent again: it moves to the end, sending. */
  | { type: 'sendRetried'; channelId: string; tempId: string; createdAt: string }
  /** `chat:message`: somebody else's message in a channel we are joined to. */
  | { type: 'messageReceived'; channelId: string; message: ChatMessage }
  /** `chat:activity`: a channel has a new message. */
  | { type: 'activity'; channel: ChatChannel }
  | { type: 'olderRequested'; channelId: string }
  /** The page asked for with `before`; applied only while that is still the cursor. */
  | { type: 'olderLoaded'; before: string; page: ChatGetMessagesReply }
  | { type: 'olderFailed'; channelId: string; before: string }
  | { type: 'noticeShown'; message: string }
  | { type: 'noticeDismissed' };

export const initialChatState: ChatState = {
  channels: [],
  unreadCounts: {},
  isOpen: false,
  activeChannelId: null,
  histories: {},
  notice: null
};

// =============================================================================
// Reading the state
// =============================================================================

/** Whether a channel is on screen: the drawer is open and showing it. */
export function isViewing(state: ChatState, channelId: string): boolean {
  return state.isOpen && state.activeChannelId === channelId;
}

/** Every unread message on the island, for the chat button's badge. */
export function selectTotalUnread(state: ChatState): number {
  return Object.values(state.unreadCounts).reduce((sum, count) => sum + count, 0);
}

export function selectActiveChannel(state: ChatState): ChatChannel | null {
  return state.channels.find((channel) => channel.id === state.activeChannelId) ?? null;
}

export function selectActiveHistory(state: ChatState): ChannelHistory | null {
  return state.activeChannelId ? (state.histories[state.activeChannelId] ?? null) : null;
}

// =============================================================================
// Merging what the server sends with what is held
// =============================================================================

/**
 * Merge a channel's latest history (a join's answer) with the messages held
 * for it. The history is authoritative for what it contains. A held message it
 * lacks is kept if it is our own unsaved one or falls inside the history's
 * window: then it was committed after the history was read. The window is
 * measured from its FIRST message, not its last, because `createdAt` is
 * stamped at insert rather than commit (and truncated to the millisecond), so
 * a later-committed message can sort level with or before the history's last
 * one. Held messages before the window are older history it trimmed (#112).
 */
function mergeHistory(history: ChatMessage[], held: ClientChatMessage[]): ClientChatMessage[] {
  const historyIds = new Set(history.map((message) => message.id));
  const first = history[0];
  const extra = held.filter(
    (message) =>
      !historyIds.has(message.id) &&
      (message.status !== undefined || !first || message.createdAt >= first.createdAt)
  );
  return extra.length ? [...history, ...extra] : history;
}

/** The first message from someone else created after `lastRead`, or null. */
function firstUnreadId(messages: ChatMessage[], lastRead: string, selfId: string): string | null {
  const readUntil = Date.parse(lastRead);
  const first = messages.find(
    (message) => message.userId !== selfId && Date.parse(message.createdAt) > readUntil
  );
  return first?.id ?? null;
}

function emptyHistory(): ChannelHistory {
  return { messages: [], hasMore: false, join: 'joining', older: 'idle' };
}

/** The channel's history after a join answered with `view`. */
function joinedHistory(
  held: ChannelHistory | undefined,
  view: ChatChannelView,
  dividerBeforeId: string | null | undefined
): ChannelHistory {
  return {
    messages: mergeHistory(view.messages, held?.messages ?? []),
    hasMore: view.hasMore,
    oldestMessageId: view.oldestMessageId,
    join: 'joined',
    older: 'idle',
    dividerBeforeId
  };
}

// =============================================================================
// Small immutable helpers
// =============================================================================

function withoutKey(record: Record<string, number>, key: string): Record<string, number> {
  if (!(key in record)) {
    return record;
  }
  const next = { ...record };
  delete next[key];
  return next;
}

function withHistory(state: ChatState, channelId: string, history: ChannelHistory): ChatState {
  return { ...state, histories: { ...state.histories, [channelId]: history } };
}

/** Apply `change` to a channel's history; nothing happens for a channel not held. */
function updateHistory(
  state: ChatState,
  channelId: string,
  change: (history: ChannelHistory) => ChannelHistory
): ChatState {
  const history = state.histories[channelId];
  return history ? withHistory(state, channelId, change(history)) : state;
}

function updateMessages(
  state: ChatState,
  channelId: string,
  change: (messages: ClientChatMessage[]) => ClientChatMessage[]
): ChatState {
  return updateHistory(state, channelId, (history) => ({ ...history, messages: change(history.messages) }));
}

/** Show a channel: it becomes the active one, its unread count goes, its join is on its way. */
function openChannel(state: ChatState, channelId: string): ChatState {
  const held = state.histories[channelId];
  return withHistory(
    {
      ...state,
      activeChannelId: channelId,
      unreadCounts: withoutKey(state.unreadCounts, channelId),
      notice: null
    },
    channelId,
    // Held messages show at once; the divider waits for the join's answer
    { ...(held ?? emptyHistory()), join: 'joining', dividerBeforeId: undefined }
  );
}

// =============================================================================
// The reducer
// =============================================================================

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'reset':
      return initialChatState;

    case 'initialState': {
      const { channels, unreadCounts } = action.payload;
      const activeChannelId = channels.some((channel) => channel.id === state.activeChannelId)
        ? state.activeChannelId
        : null;
      const next = { ...state, channels, activeChannelId };
      return {
        ...next,
        // The recount may predate the join that marked the channel on screen read
        unreadCounts:
          activeChannelId && isViewing(next, activeChannelId) ? withoutKey(unreadCounts, activeChannelId) : unreadCounts
      };
    }

    case 'drawerOpened': {
      const opened = { ...state, isOpen: true };
      return action.channelId ? openChannel(opened, action.channelId) : opened;
    }

    case 'drawerClosed':
      return { ...state, isOpen: false, notice: null };

    case 'channelSelected':
      return openChannel(state, action.channelId);

    case 'joinRequested': {
      const held = state.histories[action.channelId];
      return withHistory(state, action.channelId, { ...(held ?? emptyHistory()), join: 'joining' });
    }

    case 'joinSucceeded': {
      const { view, selfId } = action;
      const held = state.histories[view.channelId];
      const dividerBeforeId =
        held?.dividerBeforeId === undefined ? firstUnreadId(view.messages, view.lastRead, selfId) : held.dividerBeforeId;
      const next = withHistory(state, view.channelId, joinedHistory(held, view, dividerBeforeId));
      return isViewing(next, view.channelId)
        ? { ...next, unreadCounts: withoutKey(next.unreadCounts, view.channelId) }
        : next;
    }

    case 'joinFailed':
      return updateHistory(state, action.channelId, (history) => ({ ...history, join: 'failed' }));

    case 'dmStarted': {
      const { reply, selfId } = action;
      const { channel } = reply;
      const listed = state.channels.some((known) => known.id === channel.id)
        ? state
        : { ...state, channels: [...state.channels, channel] };
      // Opening a DM is opening a channel: it gets a divider of its own
      const history = joinedHistory(
        listed.histories[channel.id],
        reply,
        firstUnreadId(reply.messages, reply.lastRead, selfId)
      );
      if (!listed.isOpen) {
        return withHistory(listed, channel.id, history);
      }
      return withHistory(openChannel(listed, channel.id), channel.id, history);
    }

    case 'messageSent': {
      const held = state.histories[action.channelId] ?? emptyHistory();
      return withHistory(state, action.channelId, { ...held, messages: [...held.messages, action.message] });
    }

    case 'sendSucceeded': {
      const { tempId, message } = action;
      return updateMessages(state, action.channelId, (messages) => {
        // Already held (a join's history delivered it first): only the copy goes
        if (messages.some((held) => held.id === message.id)) {
          return messages.filter((held) => held.id !== tempId);
        }
        const index = messages.findIndex((held) => held.id === tempId);
        if (index === -1) {
          return [...messages, message];
        }
        const next = [...messages];
        next[index] = message;
        return next;
      });
    }

    case 'sendFailed':
      return updateMessages(state, action.channelId, (messages) =>
        messages.map((held) => (held.id === action.tempId ? { ...held, status: 'failed' } : held))
      );

    case 'sendRetried':
      return updateMessages(state, action.channelId, (messages) => {
        const failed = messages.find((held) => held.id === action.tempId);
        if (!failed) {
          return messages;
        }
        return [
          ...messages.filter((held) => held.id !== action.tempId),
          { ...failed, status: 'sending', createdAt: action.createdAt }
        ];
      });

    case 'messageReceived': {
      const { message } = action;
      return updateMessages(state, action.channelId, (messages) =>
        messages.some((held) => held.id === message.id) ? messages : [...messages, message]
      );
    }

    case 'activity': {
      const { channel } = action;
      const listed = state.channels.some((known) => known.id === channel.id)
        ? state
        : { ...state, channels: [...state.channels, channel] };
      if (isViewing(listed, channel.id)) {
        return listed;
      }
      return {
        ...listed,
        unreadCounts: { ...listed.unreadCounts, [channel.id]: (listed.unreadCounts[channel.id] ?? 0) + 1 }
      };
    }

    case 'olderRequested':
      return updateHistory(state, action.channelId, (history) => ({ ...history, older: 'loading' }));

    case 'olderLoaded': {
      const { before, page } = action;
      return updateHistory(state, page.channelId, (history) => {
        // A join replaced the window meanwhile: this page may not join onto it
        if (history.oldestMessageId !== before) {
          return { ...history, older: 'idle' };
        }
        const held = new Set(history.messages.map((message) => message.id));
        return {
          ...history,
          messages: [...page.messages.filter((message) => !held.has(message.id)), ...history.messages],
          hasMore: page.hasMore,
          oldestMessageId: page.oldestMessageId ?? history.oldestMessageId,
          older: 'idle'
        };
      });
    }

    case 'olderFailed':
      return updateHistory(state, action.channelId, (history) => ({
        ...history,
        older: history.oldestMessageId === action.before ? 'failed' : 'idle'
      }));

    case 'noticeShown':
      return { ...state, notice: action.message };

    case 'noticeDismissed':
      return { ...state, notice: null };
  }
}
